import { CloseIcon } from '@/assets/images';
import { AppIcon, AppInput } from '@/components';
import { useLanguage } from '@/localization/useLanguage';
import PropTypes from 'prop-types';
import { memo, useCallback } from 'react';

export const EmailInviteModal = memo(({ viewModel }) => {
   const { t } = useLanguage();
   const {
      emails: recipientEmails = [],
      emailInput: emailDraft = '',
      invalidEmails: invalidRecipientEmails = [],
      failedEmails: failedRecipientEmails = [],
      isLoading: isSendInviteLoading,
      canSend: canSendInvite,
      onInputChange: onEmailDraftChange,
      onInputBlur: onEmailDraftBlur,
      onInputKeyDown: onEmailDraftKeyDown,
      onRemoveEmail: onRecipientRemove,
      onSend: onSendInvites,
      onClose: onCloseModal,
   } = viewModel;
   const removeLabel = t('invite.REMOVE_EMAIL');
   const handleRecipientRemove = useCallback(
      email => {
         onRecipientRemove(email);
      },
      [onRecipientRemove],
   );

   const handleInputChange = useCallback(
      event => {
         onEmailDraftChange(event.target.value);
      },
      [onEmailDraftChange],
   );

   return (
      <div className="invite-email-modal invite-screen--email ct-flex ct-fcol ct-gap16">
         <div className="ct-flex ct-fcol ct-gap36">
            <div className="invite-email-header ct-flex ct-aiStart ct-jcBetween ct-gap8">
               <div className="ct-flex ct-fcol ct-gap6">
                  <h3 className="invite-email-title">{t('invite.EMAIL_TITLE')}</h3>
                  <p className="invite-email-subtitle">{t('invite.EMAIL_SUBTITLE')}</p>
               </div>
               <button
                  type="button"
                  className="invite-email-close ct-cursor ct-flexCenter"
                  onClick={onCloseModal}
                  aria-label={t('common.CLOSE')}
               >
                  <AppIcon size="sm-2" icon={<CloseIcon />} />
               </button>
            </div>

            <div className="ct-flex ct-fcol ct-gap12">
               {recipientEmails.length > 0 && (
                  <div className="invite-email-chip-box ct-flex ct-fwrap ct-gap8">
                     {recipientEmails.map(email => (
                        <span key={email} className="invite-email-chip ct-flex ct-aiCenter ct-gap8">
                           <span className="invite-email-text" title={email}>
                              {email}
                           </span>
                           <button
                              type="button"
                              className="invite-chip-remove ct-cursor ct-flexCenter"
                              onMouseDown={e => e.preventDefault()}
                              onClick={() => handleRecipientRemove(email)}
                              aria-label={removeLabel}
                           >
                              <AppIcon size="sm-2" icon={<CloseIcon />} />
                           </button>
                        </span>
                     ))}
                  </div>
               )}

               <div className="invite-email-input-group ct-flex ct-fcol ct-gap8">
                  <div className="invite-email-input-box ct-flex ct-aiCenter">
                     <AppInput
                        id="invite-email-input"
                        className="invite-email-input"
                        value={emailDraft}
                        onChange={handleInputChange}
                        onBlur={onEmailDraftBlur}
                        onKeyDown={onEmailDraftKeyDown}
                        placeholder={t('invite.EMAIL_INPUT_PLACEHOLDER')}
                        aria-label={t('invite.EMAIL_INPUT_PLACEHOLDER')}
                        autoComplete="off"
                     />
                  </div>

                  <p className="invite-email-hint">{t('invite.EMAIL_INPUT_HINT')}</p>

                  {invalidRecipientEmails.length > 0 && (
                     <p className="invite-email-error">
                        {t('invite.EMAIL_INVALID', { emails: invalidRecipientEmails.join(', ') })}
                     </p>
                  )}

                  {failedRecipientEmails.length > 0 && (
                     <p className="invite-email-error">
                        {t('invite.EMAIL_SEND_FAILED', { emails: failedRecipientEmails.join(', ') })}
                     </p>
                  )}
               </div>
            </div>
         </div>

         <div className="invite-modal-actions ct-flex ct-gap8 ct-jcEnd ct-aiCenter">
            <button
               type="button"
               className="invite-primary-btn ct-cursor ct-flex ct-aiCenter ct-jcCenter"
               onMouseDown={e => e.preventDefault()}
               onClick={onSendInvites}
               disabled={!canSendInvite || isSendInviteLoading}
            >
               {isSendInviteLoading ? t('common.PROCESSING') : t('invite.SEND_INVITES')}
            </button>
         </div>
      </div>
   );
});

EmailInviteModal.propTypes = {
   viewModel: PropTypes.shape({
      emails: PropTypes.arrayOf(PropTypes.string).isRequired,
      emailInput: PropTypes.string.isRequired,
      invalidEmails: PropTypes.arrayOf(PropTypes.string),
      failedEmails: PropTypes.arrayOf(PropTypes.string),
      isLoading: PropTypes.bool,
      canSend: PropTypes.bool,
      onInputChange: PropTypes.func.isRequired,
      onInputBlur: PropTypes.func.isRequired,
      onInputKeyDown: PropTypes.func.isRequired,
      onRemoveEmail: PropTypes.func.isRequired,
      onSend: PropTypes.func.isRequired,
      onClose: PropTypes.func.isRequired,
   }).isRequired,
};
